/** Words and looks for lots, orders, movements and locations, shared by the inventory pages. */
import type { BadgeTone, IconName } from "@/components";
import { formatDate } from "@/lib/format";
import type { LocationKind, Lot, LotStatus, MovementKind, OrderStatus, SupplierOrderRef } from "./api";

export const STATUS_LOOK: Record<LotStatus, { label: string; tone: BadgeTone; icon: IconName }> = {
  on_order: { label: "On order", tone: "info", icon: "truck" },
  active: { label: "In stock", tone: "success", icon: "check" },
  empty: { label: "Empty", tone: "neutral", icon: "minus" },
  disposed: { label: "Disposed", tone: "neutral", icon: "trash" },
};

export const ORDER_STATUS_LOOK: Record<OrderStatus, { label: string; tone: BadgeTone; icon: IconName }> = {
  on_order: { label: "On order", tone: "info", icon: "truck" },
  partly_received: { label: "Partly received", tone: "warning", icon: "truck" },
  received: { label: "Received", tone: "success", icon: "check" },
  cancelled: { label: "Cancelled", tone: "neutral", icon: "x" },
};

/** "Fraterworks order of Oct 11, 2026" (with its number when known: "#1042"). */
export function orderTitle(order: Pick<SupplierOrderRef, "supplier_name" | "reference" | "ordered_at">): string {
  const supplier = order.supplier_name || "Supplier";
  return `${supplier} order ${order.reference ? `${order.reference} ` : ""}of ${formatDate(order.ordered_at)}`;
}

export function orderPath(orderId: string): string {
  return `/inventory/orders/${encodeURIComponent(orderId)}`;
}

/** The walk through a parcel's containers, one at a time, ending in one label run. */
export function receiveOrderPath(orderId: string, lotId?: string): string {
  const path = `/inventory/receive/${encodeURIComponent(orderId)}`;
  return lotId ? `${path}?lot=${encodeURIComponent(lotId)}` : path;
}

export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  receipt: "Received",
  consumption: "Used in a batch",
  output: "Made by a batch",
  transfer: "Moved",
  adjustment: "Adjusted",
  stocktake: "Weighed at stocktake",
  disposal: "Disposed",
  sample: "Sample taken",
};

export const LOCATION_KINDS: ReadonlyArray<{ value: LocationKind; label: string }> = [
  { value: "room", label: "Room" },
  { value: "rack", label: "Rack" },
  { value: "shelf", label: "Shelf" },
  { value: "slot", label: "Slot" },
  { value: "box", label: "Box" },
  { value: "fridge", label: "Fridge" },
];

export function locationKindLabel(kind: LocationKind): string {
  return LOCATION_KINDS.find((item) => item.value === kind)?.label ?? kind;
}

/** "10% in DPG", or "Neat" when the container holds the undiluted material. */
export function dilutionText(lot: Pick<Lot, "dilution_label">): string {
  return lot.dilution_label || "Neat";
}

/** The SPA path of a lot page (the QR target). */
export function lotPath(code: string): string {
  return `/lots/${encodeURIComponent(code)}`;
}
